// src/pages/WebUIDesign.tsx
import React, { useContext, useEffect, useState } from 'react';
import { 
  LuFileText, LuRocket, LuGlobe, LuSearch, 
  LuShoppingCart, LuFolderOpen, LuMonitor, 
  LuCheck, LuArrowRight 
} from 'react-icons/lu';
import { SidebarCollapseContext } from '../contexts/SidebarCollapseContext';
import styles from './WebUIDesign.module.css';

const webUiServices = [
  {
    icon: LuRocket,
    title: 'Landing Page Design',
    desc: 'High-converting, visually stunning landing pages for campaigns, products, and events.',
    features: [
      'Conversion-Optimized Design',
      'Mobile Responsive',
      'Fast Loading Speed',
      'Lead Capture Forms'
    ],
    variant: 'dark'
  },
  {
    icon: LuGlobe,
    title: 'Full Website Design',
    desc: 'Modern, responsive websites tailored for businesses, creators, and freelancers.',
    features: [
      'Custom Design',
      'Up to 15 Pages',
      'SEO Optimized',
      'Content Management'
    ],
    variant: 'light'
  },
  {
    icon: LuSearch,
    title: 'UI/UX Audit & Redesign',
    desc: 'Professional review and redesign of your existing site for better usability and aesthetics.',
    features: [
      'Comprehensive Audit',
      'User Experience Analysis',
      'Modern Redesign',
      'Performance Optimization'
    ],
    variant: 'dark'
  },
  {
    icon: LuShoppingCart,
    title: 'E-commerce UI',
    desc: 'Custom online store interfaces with seamless shopping experiences.',
    features: [
      'Product Catalog Design',
      'Shopping Cart System',
      'Payment Integration',
      'Order Management'
    ],
    variant: 'light'
  },
  {
    icon: LuFolderOpen,
    title: 'Portfolio & Blog UI',
    desc: 'Personal portfolios and blog layouts with interactive features and animations.',
    features: [
      'Project Showcase',
      'Blog Layout',
      'Interactive Galleries',
      'Contact Forms'
    ],
    variant: 'dark'
  },
  {
    icon: LuMonitor,
    title: 'Custom Web App UI',
    desc: 'Unique interfaces for dashboards, SaaS, and custom web applications.',
    features: [
      'Dashboard Design',
      'Data Visualization',
      'Admin Interfaces',
      'Custom Components'
    ],
    variant: 'light'
  }
];

export const WebUIDesign: React.FC = () => {
  const { isSidebarCollapsed } = useContext(SidebarCollapseContext);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <div className={`${styles.webUiPage} ${isSidebarCollapsed ? styles.collapsed : ''}`}>
      {/* Hero Section */}
      <header className={`${styles.hero} ${isVisible ? styles.fadeIn : ''}`}>
        <div className={styles.heroContent}>
          <div className={styles.heroBadge}>
            <LuCheck className={styles.badgeIcon} />
            <span>Professional Web Design</span>
          </div>
          <h1 className={styles.heroTitle}>
            Web & <span className={styles.gradientText}>UI Design</span>
          </h1>
          <p className={styles.heroSubtitle}>
            Modern, responsive websites and stunning interfaces crafted to elevate your brand, 
            engage your audience, and drive results.
          </p>
        </div>
      </header>

      {/* Services Grid */}
      <main className={styles.mainContent}>
        <div className={styles.servicesGrid}>
          {webUiServices.map((service, idx) => {
            const Icon = service.icon;
            return (
              <div 
                key={idx} 
                className={`${styles.serviceCard} ${styles[service.variant]}`}
                style={{ animationDelay: `${idx * 0.1}s` }}
              >
                <div className={styles.cardHeader}>
                  <Icon className={styles.serviceIcon} />
                </div>
                
                <h2 className={styles.serviceTitle}>{service.title}</h2>
                <p className={styles.serviceDesc}>{service.desc}</p>

                <ul className={styles.featuresList}>
                  {service.features.map((feature, i) => (
                    <li key={i} className={styles.featureItem}>
                      <LuCheck className={styles.featureIcon} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Additional Info */}
        <div className={styles.infoCard}>
          <LuFileText className={styles.infoIcon} />
          <h3 className={styles.infoTitle}>Flexible additions</h3>
          <p className={styles.infoText}>
            Need additional pages or a new interface? We can extend the system as your content, team, and customer needs grow.
          </p>
        </div>

        {/* CTA Section */}
        <section className={styles.ctaSection}>
          <div className={styles.ctaContent}>
            <h2 className={styles.ctaTitle}>Ready to Build Your Dream Website?</h2>
            <p className={styles.ctaText}>
              Start with a conversation. We'll help you create a site that's beautiful, 
              fast, and user-focused.
            </p>
            <a
              href="mailto:Admin@prismaops.co.za?subject=Web & UI Design Enquiry&body=Hi PrismaOps Team,%0D%0A%0D%0AI'd like to discuss a web or UI design project.%0D%0A%0D%0APlease contact me with more details.%0D%0A%0D%0AThank you!"
              className={styles.ctaButton}
            >
              Start a Conversation <LuArrowRight className={styles.buttonIcon} />
            </a>
          </div>
        </section>
      </main>
    </div>
  );
};
